import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  MessageCircle, Package, ChevronLeft, ChevronRight, X, IndianRupee,
  Instagram, MapPin, Phone, Mail, Copy, Share2, Download, ArrowRight,
  Award, ShieldCheck, Menu, Sparkles,
} from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = process.env.REACT_APP_BACKEND_URL;

// ============ SHARED CHROME ============
function SiteChrome({ code, company, children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  const wa = company?.profile?.whatsapp;

  const NavLink = ({ to, label }) => (
    <Link to={to} className={`px-3 py-2 text-sm font-medium tracking-wide transition-colors ${loc.pathname === to ? "text-[#0F5E39]" : "text-[#0F1E15] hover:text-[#0F5E39]"}`}>{label}</Link>
  );

  const nav = [
    { to: `/store/${code}`, label: "Home" },
    { to: `/store/${code}/collections`, label: "Collections" },
    { to: `/store/${code}/products`, label: "Catalog" },
    { to: `/store/${code}/about`, label: "About" },
    { to: `/store/${code}/contact`, label: "Contact" },
  ];

  return (
    <div className="min-h-screen bg-[#FAF7EF]" data-testid="site-chrome">
      <header className="sticky top-0 z-40 bg-[#FAF7EF]/90 backdrop-blur-xl border-b border-[#E4DFCF]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
          <Link to={`/store/${code}`} className="inline-flex" data-testid="site-brand">
            <BrandLogo size={44} showWordmark/>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map(n => <NavLink key={n.to} {...n}/>)}
          </nav>
          <div className="flex items-center gap-2">
            {wa && (
              <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"
                 className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white text-sm font-medium" data-testid="site-wa-cta">
                <MessageCircle size={14}/> WhatsApp
              </a>
            )}
            <button className="md:hidden p-2 rounded-lg hover:bg-[#F1EDDF]" onClick={() => setMenuOpen(true)} data-testid="site-menu-btn"><Menu size={20}/></button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/40" onClick={()=>setMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-72 bg-white p-6" onClick={(e)=>e.stopPropagation()} data-testid="site-mobile-menu">
            <div className="flex items-center justify-between mb-6">
              <BrandLogo size={36} showWordmark/>
              <button onClick={()=>setMenuOpen(false)} className="p-1"><X size={20}/></button>
            </div>
            <div className="space-y-1">
              {nav.map(n => (
                <Link key={n.to} to={n.to} onClick={()=>setMenuOpen(false)} className={`block px-3 py-3 rounded-lg text-base ${loc.pathname === n.to ? "bg-[#F1EDDF] text-[#0F5E39]" : "text-[#0F1E15] hover:bg-[#F1EDDF]"}`}>{n.label}</Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <main>{children}</main>

      <SiteFooter company={company}/>
    </div>
  );
}

function SiteFooter({ company }) {
  const p = company?.profile || {};
  return (
    <footer className="mt-24 bg-[#083F22] text-[#FAF7EF]">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <BrandLogo size={44} showWordmark tagline variant="dark"/>
          <p className="mt-5 text-sm text-[#FAF7EF]/70 leading-relaxed max-w-md">
            {p.story?.slice(0, 220)}{p.story?.length > 220 ? "…" : ""}
          </p>
          {(p.badges || []).length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {(p.badges || []).map(b => <span key={b} className="text-[10px] uppercase tracking-widest bg-white/10 border border-white/15 text-[#C9A24B] px-2.5 py-1 rounded-full">{b}</span>)}
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">Visit</div>
          <div className="mt-3 space-y-3 text-sm text-[#FAF7EF]/80">
            {p.address && <div className="flex items-start gap-2"><MapPin size={14} className="text-[#C9A24B] mt-0.5 shrink-0"/><span>{p.address}</span></div>}
            {p.phone && <a href={`tel:${p.phone.replace(/\s/g,'')}`} className="flex items-start gap-2 hover:text-[#C9A24B]"><Phone size={14} className="text-[#C9A24B] mt-0.5 shrink-0"/><span>{p.phone}</span></a>}
            {p.email && <a href={`mailto:${p.email}`} className="flex items-start gap-2 hover:text-[#C9A24B]"><Mail size={14} className="text-[#C9A24B] mt-0.5 shrink-0"/><span>{p.email}</span></a>}
            {p.instagram && <a href={p.instagram} target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-[#C9A24B]"><Instagram size={14} className="text-[#C9A24B] mt-0.5 shrink-0"/><span>@tarujewels</span></a>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">Discover</div>
          <div className="mt-3 space-y-2 text-sm">
            <Link to={`/store/${company?.code}/collections`} className="block hover:text-[#C9A24B]">Our Collections</Link>
            <Link to={`/store/${company?.code}/products`} className="block hover:text-[#C9A24B]">Full Catalog</Link>
            <Link to={`/store/${company?.code}/about`} className="block hover:text-[#C9A24B]">Our Story</Link>
            <Link to={`/store/${company?.code}/contact`} className="block hover:text-[#C9A24B]">Contact</Link>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-6 text-[11px] flex flex-wrap items-center justify-between gap-3 text-[#FAF7EF]/50">
          <div>© {new Date().getFullYear()} {p.legal_name || company?.name} · All rights reserved</div>
          <div>Crafted with heritage in Mumbai</div>
        </div>
      </div>
    </footer>
  );
}

// ============ HOME ============
export function StorefrontHomePage() {
  const { companyCode } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || "Not found" });
    }
  })(); }, [companyCode]);

  if (state.loading) return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center text-[#4A5A50]">Loading…</div>;
  if (state.error) return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center p-8"><div className="max-w-md text-center"><div className="font-serif text-3xl">Store not found</div><div className="text-[#4A5A50] mt-2">{state.error}</div></div></div>;

  const { company, featured_products = [], collections = [] } = state.data;
  const p = company.profile || {};

  return (
    <SiteChrome code={companyCode} company={company}>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#083F22] via-[#0F5E39] to-[#083F22]"/>
        {p.hero_image_url && <img src={p.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35"/>}
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-24 lg:py-32 text-white">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#C9A24B]">{p.tagline || "We Shape Gold"}</div>
            <h1 className="mt-4 font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight">
              {p.hero_headline || `Heritage crafted at ${company.name}`}
            </h1>
            {p.hero_subline && <p className="mt-6 text-lg text-white/85 leading-relaxed max-w-xl" data-testid="home-subline">{p.hero_subline}</p>}
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to={`/store/${companyCode}/collections`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A24B] hover:bg-[#B08F3D] text-[#083F22] font-medium" data-testid="home-cta-collections">
                Explore Collections <ArrowRight size={16}/>
              </Link>
              {p.whatsapp && (
                <a href={`https://wa.me/${p.whatsapp}?text=${encodeURIComponent('Hi, I saw your website and would like to connect.')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/25 hover:bg-white/10 text-white font-medium">
                  <MessageCircle size={16}/> WhatsApp us
                </a>
              )}
            </div>
            {(p.badges || []).length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {p.badges.map(b => (
                  <span key={b} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-white/8 border border-white/15 text-[#F6EED8] px-3 py-1.5 rounded-full">
                    <Award size={11} className="text-[#C9A24B]"/> {b}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* USPS strip */}
      {(p.usps || []).length > 0 && (
        <section className="border-b border-[#E4DFCF] bg-white">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {p.usps.slice(0, 6).map((u, i) => (
              <div key={i} className="text-center" data-testid={`home-usp-${i}`}>
                <ShieldCheck size={20} className="text-[#C9A24B] mx-auto" strokeWidth={1.5}/>
                <div className="mt-2 text-xs text-[#0F1E15] font-medium leading-snug">{u}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* COLLECTIONS */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Our Collections</div>
            <h2 className="font-serif text-4xl lg:text-5xl mt-2">Every occasion, immortalised in gold.</h2>
          </div>
          <Link to={`/store/${companyCode}/collections`} className="text-sm font-medium text-[#0F5E39] hover:text-[#083F22]">View all →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.slice(0, 6).map(c => (
            <Link key={c.id} to={`/store/${companyCode}/collections/${c.id}`} className="group block rounded-2xl overflow-hidden border border-[#E4DFCF] bg-white card-hover" data-testid={`home-collection-${c.name}`}>
              <div className="aspect-[4/5] bg-[#F1EDDF] overflow-hidden">
                {c.cover_image ? (
                  <img src={fileUrl(c.cover_image)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                ) : <div className="w-full h-full flex items-center justify-center"><Package size={40} className="text-[#C9A24B]"/></div>}
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]">{c.tagline || "Signature Collection"}</div>
                <h3 className="font-serif text-2xl mt-1">{c.name}</h3>
                {c.description && <p className="text-sm text-[#4A5A50] mt-2 leading-relaxed line-clamp-2">{c.description}</p>}
                <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-[#0F5E39]">Explore <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform"/></div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured_products.length > 0 && (
        <section className="border-t border-[#E4DFCF] bg-white">
          <div className="max-w-6xl mx-auto px-5 lg:px-8 py-20">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Featured pieces</div>
                <h2 className="font-serif text-4xl lg:text-5xl mt-2">Hand-picked this season.</h2>
              </div>
              <Link to={`/store/${companyCode}/products`} className="text-sm font-medium text-[#0F5E39] hover:text-[#083F22]">Full catalog →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {featured_products.slice(0, 8).map(pr => <ProductCard key={pr.id} p={pr} code={companyCode}/>)}
            </div>
          </div>
        </section>
      )}

      {/* FOUNDER */}
      {(p.founder_name || p.founder_quote) && (
        <section className="bg-[#F1EDDF]">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 py-24 text-center">
            <Sparkles size={20} className="text-[#C9A24B] mx-auto"/>
            <blockquote className="mt-6 font-serif text-2xl lg:text-4xl leading-tight text-[#0F1E15] italic" data-testid="home-founder-quote">
              "{p.founder_quote}"
            </blockquote>
            <div className="mt-8">
              <div className="font-serif text-lg">{p.founder_name}</div>
              <div className="text-xs text-[#4A5A50] mt-1">{p.founder_title}</div>
            </div>
          </div>
        </section>
      )}
    </SiteChrome>
  );
}

// ============ ProductCard used across pages ============
function ProductCard({ p, code }) {
  return (
    <Link to={`/store/${code}/products/${p.id}`} className="group block bg-white border border-[#E4DFCF] rounded-xl overflow-hidden card-hover" data-testid={`product-card-${p.design_code || p.id}`}>
      <div className="aspect-square bg-[#F1EDDF] overflow-hidden">
        {p.media?.[0] ? <img src={fileUrl(p.media[0].thumb_url || p.media[0].url)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> : <div className="w-full h-full flex items-center justify-center"><Package size={32} className="text-[#C9A24B]"/></div>}
      </div>
      <div className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-[#0F5E39] font-semibold">{p.design_code || p.sku || "—"}</div>
        <div className="font-serif text-lg mt-0.5 line-clamp-1">{p.title}</div>
        <div className="text-xs text-[#4A5A50] mt-1">{p.metal} {p.purity || ""}</div>
        <div className="mt-3 flex items-end justify-between">
          <span className="font-serif text-lg font-medium text-[#6E5416]">{p.weight_grams ?? p.net_weight ?? "—"} g</span>
          {p.is_price_visible && <span className="text-sm font-medium text-[#C9A24B]"><IndianRupee size={11} className="inline mr-0.5"/>{(p.computed_price || 0).toLocaleString('en-IN')}</span>}
        </div>
      </div>
    </Link>
  );
}

// ============ Products list page ============
export function StorefrontProductsPage() {
  const { companyCode } = useParams();
  const [company, setCompany] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [metal, setMetal] = useState("");

  useEffect(() => { (async () => {
    const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}`);
    setCompany(data.company);
  })(); }, [companyCode]);
  useEffect(() => { (async () => {
    const params = { page_size: 60 };
    if (q) params.search = q;
    if (metal) params.metal = metal;
    const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}/products`, { params });
    setItems(data.items || []);
  })(); }, [companyCode, q, metal]);

  return (
    <SiteChrome code={companyCode} company={company}>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">The full catalog</div>
        <h1 className="font-serif text-4xl lg:text-5xl mt-2">Every piece we craft.</h1>
        <div className="mt-8 flex flex-wrap gap-3">
          <Input placeholder="Search designs…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-sm rounded-full bg-white" data-testid="site-products-search"/>
          <div className="flex gap-2">
            {["", "gold", "silver", "platinum"].map(m => (
              <button key={m} onClick={()=>setMetal(m)} className={`px-4 py-2 text-xs font-medium uppercase tracking-widest rounded-full border transition-colors ${metal === m ? "bg-[#0F5E39] text-white border-[#0F5E39]" : "bg-white border-[#E4DFCF] text-[#4A5A50] hover:bg-[#F1EDDF]"}`}>{m || "All"}</button>
            ))}
          </div>
        </div>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map(p => <ProductCard key={p.id} p={p} code={companyCode}/>)}
        </div>
        {items.length === 0 && <div className="text-center py-20 text-[#4A5A50]">No pieces match your filters.</div>}
      </section>
    </SiteChrome>
  );
}

// ============ Collections list ============
export function StorefrontCollectionsPage() {
  const { companyCode } = useParams();
  const [state, setState] = useState({ company: null, collections: [] });
  useEffect(() => { (async () => {
    const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}`);
    setState({ company: data.company, collections: data.collections });
  })(); }, [companyCode]);

  return (
    <SiteChrome code={companyCode} company={state.company}>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Signature lines</div>
        <h1 className="font-serif text-4xl lg:text-5xl mt-2">Our Collections</h1>
        <p className="text-[#4A5A50] mt-3 max-w-2xl">Five distinct lines, each with its own story — from temple heirlooms to everyday elegance.</p>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {state.collections.map(c => (
            <Link key={c.id} to={`/store/${companyCode}/collections/${c.id}`} className="group block rounded-2xl overflow-hidden border border-[#E4DFCF] bg-white card-hover" data-testid={`collection-${c.name}`}>
              <div className="aspect-[4/5] bg-[#F1EDDF] overflow-hidden">
                {c.cover_image ? <img src={fileUrl(c.cover_image)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/> : null}
              </div>
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C9A24B]">{c.tagline || "Collection"}</div>
                <h3 className="font-serif text-3xl mt-1">{c.name}</h3>
                {c.description && <p className="text-sm text-[#4A5A50] mt-3 leading-relaxed line-clamp-3">{c.description}</p>}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </SiteChrome>
  );
}

// ============ Collection detail (with QuickSell-style share) ============
export function StorefrontCollectionPage() {
  const { companyCode, collectionId } = useParams();
  const [state, setState] = useState({ loading: true, data: null });
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}/collections/${collectionId}`);
      const info = await axios.get(`${API}/api/public/storefront/${companyCode}`);
      setState({ loading: false, data: { ...data, company: info.data.company } });
    } catch (e) { setState({ loading: false, data: null }); }
  })(); }, [companyCode, collectionId]);

  if (state.loading || !state.data) return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center text-[#4A5A50]">Loading…</div>;
  const { collection, items = [], company } = state.data;

  const currentUrl = window.location.href;

  return (
    <SiteChrome code={companyCode} company={company}>
      <section className="relative">
        {collection.cover_image && (
          <div className="relative h-72 lg:h-96 overflow-hidden">
            <img src={fileUrl(collection.cover_image)} alt="" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#083F22]/70 to-transparent"/>
          </div>
        )}
        <div className="max-w-6xl mx-auto px-5 lg:px-8 -mt-16 lg:-mt-24 relative">
          <div className="bg-white border border-[#E4DFCF] rounded-2xl p-8 lg:p-10 shadow-lg">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#C9A24B]">{collection.tagline || "Collection"}</div>
                <h1 className="font-serif text-4xl lg:text-6xl mt-2" data-testid="collection-title">{collection.name}</h1>
                {collection.description && <p className="text-[#4A5A50] mt-4 leading-relaxed max-w-2xl">{collection.description}</p>}
              </div>
              <ShareButton url={currentUrl} title={`${collection.name} · ${company.name}`} onOpenDialog={() => setShareOpen(true)}/>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {items.map(p => <ProductCard key={p.id} p={p} code={companyCode}/>)}
        </div>
      </section>

      <QuickSellShareDialog
        open={shareOpen}
        setOpen={setShareOpen}
        url={currentUrl}
        title={`${collection.name} · ${company.name}`}
        note={collection.description}
        wa={company.profile?.whatsapp}
      />
    </SiteChrome>
  );
}

// ============ Product detail (with QuickSell-style share + gallery lightbox) ============
export function StorefrontProductPage() {
  const { companyCode, productId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });
  const [lightbox, setLightbox] = useState(null); // { index }
  const [shareOpen, setShareOpen] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => { (async () => {
    try {
      const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}/products/${productId}`);
      setState({ loading: false, data });
    } catch (e) { setState({ loading: false, data: null }); }
  })(); }, [companyCode, productId]);

  if (state.loading) return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center text-[#4A5A50]">Loading…</div>;
  if (!state.data) return <div className="min-h-screen bg-[#FAF7EF] flex items-center justify-center"><div className="text-center"><div className="font-serif text-3xl">Piece not found</div><button onClick={()=>nav(-1)} className="mt-4 underline text-[#0F5E39]">Go back</button></div></div>;

  const { product: p, company } = state.data;
  const media = p.media || [];
  const currentUrl = window.location.href;

  const enquireOnWA = () => {
    const wa = company.profile?.whatsapp;
    if (!wa) return toast.error("WhatsApp not configured");
    const text = encodeURIComponent(
      `Hi, interested in *${p.design_code || p.title}*.\n\n${p.title} · ${p.metal} ${p.purity || ''} · ${p.weight_grams ?? p.net_weight ?? '?'} g${p.is_price_visible ? ` · ₹${(p.computed_price || 0).toLocaleString('en-IN')}` : ''}\n\n${msg ? msg + '\n\n' : ''}${currentUrl}`
    );
    window.open(`https://wa.me/${wa}?text=${text}`, "_blank", "noopener,noreferrer");
    // Also log inquiry
    axios.post(`${API}/api/public/storefront/${companyCode}/inquiries`, {
      product_id: p.id,
      message: msg,
      contact_name: "Website visitor",
    }).catch(()=>{});
    setEnquireOpen(false);
  };

  return (
    <SiteChrome code={companyCode} company={company}>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
        <button onClick={() => nav(-1)} className="text-sm text-[#4A5A50] hover:text-[#0F1E15] inline-flex items-center gap-1 mb-6"><ChevronLeft size={14}/> Back</button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Media */}
          <div>
            <button onClick={() => setLightbox({ index: 0 })} className="block w-full aspect-square bg-[#F1EDDF] rounded-2xl overflow-hidden" data-testid="product-hero-image">
              {media[0] ? <img src={fileUrl(media[0].url || media[0].thumb_url)} alt={p.title} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center"><Package size={64} className="text-[#C9A24B]"/></div>}
            </button>
            {media.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {media.map((m, i) => (
                  <button key={i} onClick={() => setLightbox({ index: i })} className="w-20 h-20 shrink-0 rounded-lg overflow-hidden border border-[#E4DFCF]" data-testid={`product-thumb-${i}`}>
                    <img src={fileUrl(m.thumb_url || m.url)} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Details */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#0F5E39] font-semibold" data-testid="product-design-code">{p.design_code || p.sku || "—"}</div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-2">{p.title}</h1>
            {p.labels?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {p.labels.map(l => <span key={l} className="text-[10px] uppercase tracking-widest bg-[#F6EED8] text-[#6E5416] px-2.5 py-1 rounded-full">{l}</span>)}
              </div>
            )}
            {p.description && <p className="text-[#4A5A50] mt-6 leading-relaxed">{p.description}</p>}
            <div className="mt-8 grid grid-cols-2 gap-6 py-6 border-y border-[#E4DFCF]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Weight</div>
                <div className="font-serif text-4xl mt-1 text-[#6E5416]">{p.weight_grams ?? p.net_weight ?? "—"} <span className="text-xl">g</span></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Metal</div>
                <div className="font-serif text-xl mt-1">{p.metal} {p.purity || ""}</div>
              </div>
              {p.is_price_visible && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Indicative price</div>
                  <div className="font-serif text-4xl mt-1 text-[#0F5E39]"><IndianRupee size={20} className="inline mr-1"/>{(p.computed_price || 0).toLocaleString('en-IN')}</div>
                  <div className="text-[11px] text-[#4A5A50] mt-1">Final price depends on live gold rate + making. Enquire for exact quote.</div>
                </div>
              )}
              {p.stone_details && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Stones</div>
                  <div className="text-sm mt-1">{p.stone_details}</div>
                </div>
              )}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={()=>setEnquireOpen(true)} className="rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white h-12 px-6" data-testid="product-enquire-btn">
                <MessageCircle size={16} className="mr-2"/> Enquire on WhatsApp
              </Button>
              <Button variant="outline" onClick={()=>setShareOpen(true)} className="rounded-full h-12 px-6 border-[#E4DFCF]" data-testid="product-share-btn">
                <Share2 size={16} className="mr-2"/> Share this piece
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <Lightbox media={media} index={lightbox.index} title={p.title} designCode={p.design_code} onClose={()=>setLightbox(null)}/>
      )}

      {/* Share dialog */}
      <QuickSellShareDialog
        open={shareOpen}
        setOpen={setShareOpen}
        url={currentUrl}
        title={`${p.title} · ${p.design_code || ''}`.trim()}
        note={`${p.metal} ${p.purity || ''} · ${p.weight_grams ?? p.net_weight ?? '?'} g${p.is_price_visible ? ` · ₹${(p.computed_price || 0).toLocaleString('en-IN')}` : ''}`}
        imageUrl={media[0] ? fileUrl(media[0].url || media[0].thumb_url) : null}
        wa={company.profile?.whatsapp}
      />

      {/* Enquire dialog */}
      <Dialog open={enquireOpen} onOpenChange={setEnquireOpen}>
        <DialogContent data-testid="product-enquire-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Enquire · {p.design_code || p.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea rows={3} value={msg} onChange={(e)=>setMsg(e.target.value)} placeholder="Anything specific you'd like to know — size, weight tolerance, delivery timeline?" data-testid="product-enquire-msg"/>
            <Button onClick={enquireOnWA} className="w-full rounded-full bg-[#25D366] hover:bg-[#1FAE55] text-white" data-testid="product-enquire-send">
              <MessageCircle size={16} className="mr-2"/> Open WhatsApp with your enquiry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </SiteChrome>
  );
}

function Lightbox({ media, index: startIdx, title, designCode, onClose }) {
  const [idx, setIdx] = useState(startIdx || 0);
  const next = useCallback(() => setIdx(i => (i + 1) % media.length), [media.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + media.length) % media.length), [media.length]);
  useEffect(() => {
    const h = (e) => { if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [next, prev, onClose]);
  const m = media[idx];
  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="product-lightbox">
      <div className="absolute top-4 left-4 text-white/80">
        <div className="text-[10px] uppercase tracking-widest text-[#C9A24B]">{designCode}</div>
        <div className="font-serif text-lg">{title}</div>
      </div>
      <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={(e)=>{e.stopPropagation();onClose();}}><X size={22}/></button>
      {media.length > 1 && <>
        <button className="absolute left-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={(e)=>{e.stopPropagation();prev();}}><ChevronLeft size={22}/></button>
        <button className="absolute right-6 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={(e)=>{e.stopPropagation();next();}}><ChevronRight size={22}/></button>
      </>}
      <img src={fileUrl(m.url || m.thumb_url)} alt="" className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e)=>e.stopPropagation()}/>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs">{idx + 1} / {media.length}</div>
    </div>
  );
}

// ============ QuickSell-style share dialog ============
function QuickSellShareDialog({ open, setOpen, url, title, note, imageUrl, wa }) {
  const [copied, setCopied] = useState(false);

  const msg = `*${title}*${note ? '\n' + note : ''}\n\n${url}`;

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(()=>setCopied(false), 2000); }
    catch { toast.error("Could not copy"); }
  };
  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(msg); toast.success("Message copied"); }
    catch { toast.error("Could not copy"); }
  };
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: note || title, url }); }
      catch {}
    } else copyLink();
  };
  const downloadImage = async () => {
    if (!imageUrl) return toast.error("No image to download");
    try {
      const resp = await fetch(imageUrl, { mode: "cors" });
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.webp`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
    } catch { window.open(imageUrl, "_blank"); }
  };

  const shareTargets = [
    {
      key: "whatsapp-direct", label: "WhatsApp to shop", color: "bg-[#25D366] text-white",
      href: wa ? `https://wa.me/${wa}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`,
      icon: MessageCircle,
    },
    {
      key: "whatsapp-anywhere", label: "Share to any WhatsApp chat", color: "bg-[#F1EDDF] text-[#0F5E39]",
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(msg)}`,
      icon: MessageCircle,
    },
    {
      key: "instagram", label: "Instagram (open link)", color: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white",
      href: `https://www.instagram.com/`, icon: Instagram, onClickBefore: copyMessage,
      hint: "Message copied — paste in DM",
    },
    {
      key: "email", label: "Email", color: "bg-[#F1EDDF] text-[#0F1E15]",
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(msg)}`, icon: Mail,
    },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="quicksell-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Share this</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-[#F1EDDF] rounded-lg text-sm">
            <div className="font-medium text-[#0F1E15]">{title}</div>
            {note && <div className="text-xs text-[#4A5A50] mt-1">{note}</div>}
          </div>

          <div className="grid grid-cols-2 gap-2">
            {shareTargets.map(t => (
              <a
                key={t.key}
                href={t.href}
                target={t.key === "email" ? "_self" : "_blank"}
                rel="noreferrer"
                onClick={() => t.onClickBefore?.()}
                className={`inline-flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-medium ${t.color} hover:opacity-90 transition-opacity`}
                data-testid={`share-target-${t.key}`}
              >
                <t.icon size={16}/> {t.label}
              </a>
            ))}
          </div>

          <div className="flex gap-2">
            <Input readOnly value={url} className="font-mono text-xs" data-testid="share-url-input"/>
            <Button variant="outline" size="icon" onClick={copyLink} data-testid="share-copy-url">
              <Copy size={14}/>
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={copyMessage} className="rounded-full flex-1" data-testid="share-copy-msg">
              <Copy size={14} className="mr-1.5"/> Copy message
            </Button>
            {imageUrl && (
              <Button variant="outline" onClick={downloadImage} className="rounded-full flex-1" data-testid="share-download-image">
                <Download size={14} className="mr-1.5"/> Save image
              </Button>
            )}
            {navigator.share && (
              <Button onClick={shareNative} className="rounded-full flex-1 bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="share-native">
                <Share2 size={14} className="mr-1.5"/> More…
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ShareButton({ url, title, onOpenDialog }) {
  return (
    <button onClick={onOpenDialog} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white text-sm font-medium" data-testid="site-share-btn">
      <Share2 size={14}/> Share
    </button>
  );
}

// ============ About ============
export function StorefrontAboutPage() {
  const { companyCode } = useParams();
  const [company, setCompany] = useState(null);
  useEffect(() => { (async () => {
    const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}`);
    setCompany(data.company);
  })(); }, [companyCode]);
  if (!company) return null;
  const p = company.profile || {};

  return (
    <SiteChrome code={companyCode} company={company}>
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Our Story</div>
        <h1 className="font-serif text-4xl lg:text-6xl mt-2">Heritage. Craft. Legacy.</h1>
        {p.story && <p className="mt-8 text-lg text-[#0F1E15] leading-relaxed" data-testid="about-story">{p.story}</p>}

        {(p.founder_quote || p.founder_bio) && (
          <div className="mt-16 p-8 lg:p-10 bg-[#F1EDDF] rounded-2xl">
            <Sparkles size={20} className="text-[#C9A24B]"/>
            {p.founder_quote && <blockquote className="mt-4 font-serif text-2xl lg:text-3xl leading-tight italic text-[#0F1E15]">"{p.founder_quote}"</blockquote>}
            <div className="mt-6 pt-6 border-t border-[#E4DFCF] flex items-start gap-4">
              <div>
                <div className="font-serif text-xl">{p.founder_name}</div>
                <div className="text-xs text-[#4A5A50]">{p.founder_title}</div>
                {p.founder_bio && <p className="text-sm text-[#4A5A50] mt-3 leading-relaxed">{p.founder_bio}</p>}
              </div>
            </div>
          </div>
        )}

        {(p.badges || []).length > 0 && (
          <div className="mt-16">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Credentials</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {p.badges.map(b => <span key={b} className="inline-flex items-center gap-1.5 text-xs bg-white border border-[#E4DFCF] px-3 py-2 rounded-full text-[#0F1E15]"><Award size={12} className="text-[#C9A24B]"/> {b}</span>)}
            </div>
          </div>
        )}

        {p.mission && (
          <div className="mt-16">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Our Mission</div>
            <p className="mt-3 text-lg leading-relaxed">{p.mission}</p>
          </div>
        )}
        {p.vision && (
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Our Vision</div>
            <p className="mt-3 text-lg leading-relaxed">{p.vision}</p>
          </div>
        )}

        {(p.gallery_urls || []).length > 0 && (
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4">
            {p.gallery_urls.map((u, i) => (
              <div key={i} className="aspect-[4/5] bg-[#F1EDDF] rounded-2xl overflow-hidden">
                <img src={u} alt="" className="w-full h-full object-cover"/>
              </div>
            ))}
          </div>
        )}
      </section>
    </SiteChrome>
  );
}

// ============ Contact ============
export function StorefrontContactPage() {
  const { companyCode } = useParams();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({ contact_name: "", contact_mobile: "", message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { (async () => {
    const { data } = await axios.get(`${API}/api/public/storefront/${companyCode}`);
    setCompany(data.company);
  })(); }, [companyCode]);

  const submit = async () => {
    if (!form.contact_name || !form.message) return toast.error("Name and message required");
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/storefront/${companyCode}/inquiries`, {
        ...form, source: "storefront",
      });
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ contact_name: "", contact_mobile: "", message: "" });
    } catch (e) {
      toast.error("Failed to send");
    } finally { setBusy(false); }
  };

  if (!company) return null;
  const p = company.profile || {};

  return (
    <SiteChrome code={companyCode} company={company}>
      <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Reach us</div>
            <h1 className="font-serif text-4xl lg:text-5xl mt-2">Let's create something.</h1>
            <p className="text-[#4A5A50] mt-4 leading-relaxed">Whether you're a jeweller looking for a manufacturing partner or an admirer of fine craft — we'd love to hear from you.</p>

            <div className="mt-10 space-y-6 text-[#0F1E15]">
              {p.address && <div className="flex items-start gap-3"><MapPin size={18} className="text-[#C9A24B] shrink-0 mt-0.5"/><div><div className="text-xs uppercase tracking-widest text-[#4A5A50]">Workshop</div><div className="mt-1">{p.address}</div></div></div>}
              {p.phone && <div className="flex items-start gap-3"><Phone size={18} className="text-[#C9A24B] shrink-0 mt-0.5"/><div><div className="text-xs uppercase tracking-widest text-[#4A5A50]">Phone</div><a href={`tel:${p.phone.replace(/\s/g,'')}`} className="mt-1 block hover:text-[#0F5E39]">{p.phone}</a></div></div>}
              {p.email && <div className="flex items-start gap-3"><Mail size={18} className="text-[#C9A24B] shrink-0 mt-0.5"/><div><div className="text-xs uppercase tracking-widest text-[#4A5A50]">Email</div><a href={`mailto:${p.email}`} className="mt-1 block hover:text-[#0F5E39]">{p.email}</a></div></div>}
              {p.whatsapp && <div className="flex items-start gap-3"><MessageCircle size={18} className="text-[#25D366] shrink-0 mt-0.5"/><div><div className="text-xs uppercase tracking-widest text-[#4A5A50]">WhatsApp</div><a href={`https://wa.me/${p.whatsapp}`} target="_blank" rel="noreferrer" className="mt-1 block hover:text-[#0F5E39]">Message us directly</a></div></div>}
            </div>
          </div>

          <div className="bg-white border border-[#E4DFCF] rounded-2xl p-8">
            <div className="font-serif text-2xl">Send a message</div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A5A50]">Name</label>
                <Input value={form.contact_name} onChange={(e)=>setForm({...form, contact_name:e.target.value})} className="mt-1" data-testid="contact-name"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A5A50]">Mobile / WhatsApp</label>
                <Input value={form.contact_mobile} onChange={(e)=>setForm({...form, contact_mobile:e.target.value})} className="mt-1" data-testid="contact-mobile"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#4A5A50]">Message</label>
                <Textarea rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} className="mt-1" placeholder="Tell us about your requirement…" data-testid="contact-message"/>
              </div>
              <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white h-11" data-testid="contact-submit">
                {busy ? "Sending…" : "Send message"}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </SiteChrome>
  );
}
